import { RefObject, useLayoutEffect, useRef } from 'react';

const ROW_ATTRIBUTE = 'data-chapter-row';

/** Marks the element inside a row that the animation finds its row by. */
export const rowMarker = (id: string): Record<string, string> => ({ [ROW_ATTRIBUTE]: id });

/**
 * Slides each row from where it was to where it is now, and flashes the one that was moved,
 * so a click that swaps two rows reads as movement rather than as two labels changing.
 */
export const useRowMoveAnimation = (
  containerRef: RefObject<HTMLElement>,
  order: string,
  movedId: string | null,
): void => {
  const lastTops = useRef(new Map<string, number>());

  useLayoutEffect(() => {
    const rows = new Map<string, HTMLElement>();
    containerRef.current?.querySelectorAll<HTMLElement>(`[${ROW_ATTRIBUTE}]`).forEach(marker => {
      const row = marker.closest('tr');
      if (row) rows.set(marker.getAttribute(ROW_ATTRIBUTE) ?? '', row);
    });
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    rows.forEach((row, id) => {
      const before = lastTops.current.get(id);
      const shift = before === undefined ? 0 : before - row.offsetTop;
      // jsdom has no Web Animations API.
      if (!row.animate) return;
      if (shift !== 0 && !reduceMotion) {
        row.animate([{ transform: `translateY(${shift}px)` }, { transform: 'none' }], {
          duration: 250,
          easing: 'ease-out',
        });
      }
      if (id === movedId && shift !== 0) {
        // Opaque and raised, or the row it slides past shows through it.
        row.animate(
          [
            { position: 'relative', zIndex: 1, backgroundColor: 'var(--row-moved)' },
            { position: 'relative', zIndex: 1, backgroundColor: 'var(--card)' },
          ],
          { duration: 1200, easing: 'ease-out' },
        );
      }
    });

    lastTops.current = new Map([...rows].map(([id, row]) => [id, row.offsetTop]));
  }, [containerRef, order, movedId]);
};
